import Link from '~/components/Link';
import { useForm, usePromiseWatcher } from '~/hooks';
import { login } from '~/api/auth';
import { Button } from '~/components';
import { FormSchemaAwareWidget } from '~/components/form';
import { FormContextProvider } from '~/hooks/providers/FormContextProvider';
import { Message } from 'semantic-ui-react';
import AuthForm  from '../components/AuthForm';
import redirectAfterLogin from './redirectAfterLogin';

interface LoginFormState {
  email: string;
  password: string;
}

const LoginFormImpl = () => {
  const {formState} = useForm<LoginFormState>();
  const {execute, status} = usePromiseWatcher(
    async () => {
      // Attempt login
      const loginValue = await login(formState);
      if (loginValue?.reqwuiredEmailVerification) {
        // Redirect to email verification
        window.location.href = '/confirm-email';
        return null;
      }
      if (!loginValue) {
        throw new Error('Login is unsuccessful');
      }
      // Password OK but a second factor is still required.
      if (loginValue.requiresTwoFactor) {
        const params = new URLSearchParams(window.location.search);
        const methods = (loginValue.methods || []).join(',');
        if (methods) params.set('methods', methods);
        window.location.href = `/verify-2fa?${params.toString()}`;
        return null;
      }
      // Admin without 2FA — force enrollment before continuing.
      if (loginValue.requiresTwoFactorSetup) {
        window.location.href = `/setup-2fa${window.location.search}`;
        return null;
      }
      // Fully authenticated — redirect to the landing page.
      redirectAfterLogin(loginValue);
    },
    {
      loading: null,
      success: null,
      error: 'Login unsuccessful. Please try again.'
    },
    [formState],
    {toastOpts: { id: 'login' }} as any
  ); 

  return (
    <AuthForm header = 'Log In'>
      <FormSchemaAwareWidget
        schemaKey="email"
        fluid
        fieldLabel="E-mail Address"
        icon="envelope"
        iconPosition="left"
        placeholder="E-mail address"
        autoComplete="username"
      />
      <FormSchemaAwareWidget
        schemaKey="password"
        fluid
        fieldLabel="Password"
        icon="lock"
        iconPosition="left"
        placeholder="Password"
        type="password"
        autoComplete="current-password"
      />
      <Button 
        fluid 
        secondary
        size="large" 
        disabled={formState.email.length === 0 || formState.password.length === 0 || status ==='pending' || status === 'success'}
        loading={status === 'pending' || status === 'success'} 
        onClick={execute}
      >
        Log In
      </Button>
      <Message className="auth-page__footer-message">
        <Link to="/forgot-password">Forgot your password?</Link>
      </Message>
    </AuthForm>
    );
};

const LoginForm = () => {
  const defaults: LoginFormState = {
    email: '',
    password: ''
  };
  
  return (
    <FormContextProvider defaults={defaults}>
      <LoginFormImpl/>
    </FormContextProvider>
  );
};

export default LoginForm;
